import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Play, X } from "lucide-react";

const YT_ID = "3_c0IBnAcwU";

/**
 * HeroVideo — editorial poster card with cinematic play affordance.
 * Tap → opens a full-bleed YouTube modal (autoplay, no related videos).
 * Replaces the abstract 3D orb in the hero right column with real proof.
 */
export default function HeroVideo() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group relative w-full max-w-[560px] block card-edge overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        style={{ borderRadius: 4, aspectRatio: "4/5" }}
        aria-label="Watch the AI Innovation Arena 2026 recap"
        data-testid="hero-video-trigger"
      >
        <img
          src="/assets/hit/grand-finale.jpg"
          alt="AI Innovation Arena 2026 — Grand Finale, Haldia"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          style={{ objectPosition: "center 35%" }}
          loading="eager"
        />

        {/* Editorial dark grade for readability */}
        <span
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,14,31,0.10) 0%, rgba(10,14,31,0.25) 45%, rgba(10,14,31,0.78) 100%)",
          }}
        />

        {/* Top-left caption stamp */}
        <span
          className="absolute top-4 left-4 font-mono text-[10.5px] px-2.5 py-1"
          style={{
            color: "var(--canvas)",
            background: "rgba(10,14,31,0.55)",
            letterSpacing: "0.18em",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(250,250,247,0.18)",
          }}
        >
          <span style={{ color: "var(--accent-gold)" }}>●</span> LIVE FOOTAGE · 16 MAY 2026
        </span>

        {/* Centered play button */}
        <span
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center"
        >
          <span
            className="relative inline-flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
            style={{
              width: 76,
              height: 76,
              borderRadius: "9999px",
              background: "rgba(255,255,255,0.92)",
              boxShadow:
                "0 18px 50px -12px rgba(79,70,229,0.55), 0 0 0 1px rgba(255,255,255,0.4) inset",
            }}
          >
            <Play
              size={26}
              strokeWidth={1.5}
              style={{ color: "var(--ink)", marginLeft: 3, fill: "var(--ink)" }}
            />
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full pulse-ring"
              style={{ borderRadius: "9999px" }}
            />
          </span>
        </span>

        {/* Bottom caption block */}
        <span className="absolute left-0 right-0 bottom-0 p-5">
          <span
            className="block font-display text-[18px] md:text-[20px] leading-snug"
            style={{ color: "var(--canvas)", fontWeight: 500 }}
          >
            The AI Innovation Arena.
            <br />
            <em
              className="italic"
              style={{ color: "var(--accent-gold)", fontWeight: 400 }}
            >
              Watch how we ship cohorts, not slides.
            </em>
          </span>
          <span
            className="mt-3 inline-flex items-center gap-2 font-mono text-[11px]"
            style={{ color: "rgba(250,250,247,0.85)", letterSpacing: "0.14em" }}
          >
            <span>2 MIN RECAP</span>
            <span aria-hidden="true">·</span>
            <span>700+ TRAINED · 70 PODS</span>
          </span>
        </span>
      </button>

      {/* Modal — portal'd to <body> to escape any parent stacking context */}
      {open && typeof document !== "undefined" && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Event recap video"
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-8"
          data-testid="hero-video-modal"
          onClick={() => setOpen(false)}
        >
          <span
            aria-hidden="true"
            className="absolute inset-0"
            style={{ background: "rgba(6,8,18,0.85)", backdropFilter: "blur(8px)" }}
          />
          <div
            className="relative w-full max-w-[1080px]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close video"
              onClick={() => setOpen(false)}
              className="absolute -top-12 right-0 inline-flex items-center gap-2 text-[12px] font-mono"
              style={{ color: "rgba(250,250,247,0.8)", letterSpacing: "0.14em" }}
              data-testid="hero-video-close"
            >
              CLOSE <X size={16} strokeWidth={1.5} />
            </button>
            <div
              className="w-full overflow-hidden shadow-2xl"
              style={{ aspectRatio: "16/9", background: "#000", borderRadius: 4 }}
            >
              <iframe
                src={`https://www.youtube.com/embed/${YT_ID}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                title="AI Innovation Arena 2026 — Recap"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                style={{ border: 0 }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
